import { type ReactElement } from 'react';

/**
 * A country flag chip — the Vello Claude Design "Admin Web - Passageiros Interativo" renders each country as a
 * small flag image (an 18–20px rounded rectangle with a hairline border), not a text code or an OS emoji.
 * OS emoji flags (🇧🇷) are the wrong choice here: on Linux/Chrome they degrade to a two-letter box, so the
 * console showed bare codes. This renders the real flag as a background image (flagcdn, the source the design
 * uses) over a token surface, so a failed/slow image still shows a neutral chip. The country code is data
 * (never hardcoded), so this stays country-neutral.
 */
export function Flag({ code, size = 'md' }: { code?: string; size?: 'sm' | 'md' }): ReactElement | null {
  const cc = (code ?? '').trim().toLowerCase();
  // Only ISO 3166-1 alpha-2 codes map to a flag; anything else renders nothing (caller shows the code text).
  if (!/^[a-z]{2}$/.test(cc)) return null;
  const w = size === 'sm' ? 18 : 20;
  const h = size === 'sm' ? 13 : 14;
  return (
    <span
      role="img"
      aria-label={code}
      title={code}
      style={{
        width: w,
        height: h,
        flex: 'none',
        display: 'inline-block',
        borderRadius: 3,
        backgroundColor: 'var(--color-surface-subtle)',
        backgroundImage: `url(https://flagcdn.com/w40/${cc}.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: 'inset 0 0 0 1px var(--color-border-default)',
      }}
    />
  );
}
